import Button from "components/Button/Button";
import Card from "components/Card/Card";
import type { OptionType } from "components/inputs";
import { TextInput, MaskedInput, Select } from "components/inputs";
import { KaboodleModal } from "components/Modals";
import { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import {
  useGetAdjusterByIdQuery,
  usePatchAdjusterByIdMutation,
  useCreateAdjusterMutation,
} from "services/adjuster-api";
import { api } from "services/api";
import { useDispatch } from "utils/redux";
import { useConstants } from "utils/hooks";
import type { AdjusterType } from "types/DataTypes";

interface EditAdjusterDetailsModalProps {
  adjusterId?: number;
  clientId?: string;
  isOpen: boolean;
  onClose: () => void;
}

type FormData = {
  name: string;
  email: string;
  address1?: string;
  address2?: string;
  state?: string;
  zip?: string;
  phone?: string;
  client_id?: number;
};

const initialFormState: FormData = {
  name: "",
  email: "",
  address1: "",
  address2: "",
  state: "",
  zip: "",
  phone: "",
};

export const EditAdjusterDetailsModal = ({
  adjusterId,
  clientId,
  isOpen,
  onClose,
}: EditAdjusterDetailsModalProps) => {
  const dispatch = useDispatch();
  const { constants } = useConstants();
  const [isCreating, setIsCreating] = useState(false);

  const {
    control,
    reset,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: initialFormState,
  });

  const { data: adjuster } = useGetAdjusterByIdQuery(
    { id: adjusterId },
    { skip: !adjusterId },
  );

  useEffect(() => {
    if (adjusterId) {
      setIsCreating(false);
    } else {
      setIsCreating(true);
      reset(initialFormState);
    }
  }, [adjusterId, reset]);

  useEffect(() => {
    if (adjuster) {
      reset(adjuster);
    }
  }, [reset, adjuster]);

  const [patchAdjusterById] = usePatchAdjusterByIdMutation();
  const [createAdjuster] = useCreateAdjusterMutation();

  const onSubmit = async (data: FormData) => {
    try {
      if (isCreating) {
        // Creating a new adjuster
        const formData = {
          ...data,
          client: clientId,
        };

        const result = await createAdjuster({
          data: formData as unknown as AdjusterType,
        }).unwrap();

        if (result.id) {
          toast.success("Adjuster successfully created");
          dispatch(api.util.invalidateTags(["Clients", "Adjusters"]));
          reset(initialFormState);
          onClose();
        }
      } else {
        // Editing an existing adjuster
        const result = await patchAdjusterById({
          id: adjusterId,
          data,
        }).unwrap();

        if (result.id) {
          toast.success("Adjuster successfully updated");
          dispatch(api.util.invalidateTags(["Clients", "Adjusters"]));
          reset(initialFormState);
          onClose();
        }
      }
    } catch (err) {
      console.error("Failed to save adjuster:", err);
      toast.error(
        isCreating ? "Failed to create adjuster" : "Failed to update adjuster",
      );

      if (err.data) {
        Object.entries(err.data).forEach(([field, messages]) => {
          setError(field as keyof FormData, {
            type: "manual",
            message: Array.isArray(messages) ? messages[0] : messages,
          });
        });
      }
    }
  };

  const handleClose = useCallback(() => {
    reset(initialFormState);
    onClose();
  }, [reset, onClose]);

  return (
    <KaboodleModal
      isOpen={isOpen}
      onClose={handleClose}
      footer={
        <Button onClick={handleSubmit(onSubmit)}>
          {isCreating ? "Create Adjuster" : "Save"}
        </Button>
      }
    >
      <Card
        heading={isCreating ? "Create New Adjuster" : "Edit Adjuster Details"}
      >
        <Controller
          control={control}
          name="name"
          rules={{ required: "Adjuster name is required" }}
          render={({ field }) => (
            <TextInput
              {...field}
              name="name"
              placeholder="Name"
              label="Adjuster Name"
              errors={errors}
            />
          )}
        />
        <Controller
          control={control}
          name="email"
          rules={{ required: "Adjuster email is required" }}
          render={({ field }) => (
            <TextInput
              {...field}
              name="email"
              placeholder="name@xyz.com"
              label="Adjuster Email"
              errors={errors}
            />
          )}
        />
        <Controller
          control={control}
          name="address1"
          render={({ field }) => (
            <TextInput
              {...field}
              name="address1"
              placeholder="Street Address"
              label="Street Address"
              errors={errors}
            />
          )}
        />
        <Controller
          control={control}
          name="address2"
          render={({ field }) => (
            <TextInput
              {...field}
              name="address2"
              placeholder="APT/Suite"
              label="APT/Suite"
              errors={errors}
            />
          )}
        />
        <Controller
          control={control}
          name="state"
          render={({ field }) => (
            <Select
              {...field}
              name="state"
              label="State"
              options={constants?.STATES || []}
              errors={errors}
              value={constants?.STATES?.find(
                (option) => option.value === field.value,
              )}
              onChange={(option: OptionType) => field.onChange(option?.value)}
            />
          )}
        />
        <Controller
          control={control}
          name="zip"
          render={({ field }) => (
            <MaskedInput
              {...field}
              name="zip"
              placeholder="Zip Code"
              label="Zip"
              mask="zipcode"
              errors={errors}
            />
          )}
        />
        <Controller
          control={control}
          name="phone"
          render={({ field }) => (
            <MaskedInput
              {...field}
              name="phone"
              placeholder="Phone Number"
              label="Phone"
              mask="phone"
              errors={errors}
            />
          )}
        />
      </Card>
    </KaboodleModal>
  );
};
